import { estimateOneRepMaxG } from './one-rep-max.js';
import { beats, buildRepMaxTable, detectPersonalRecords, isDominated } from './personal-records.js';
import { effectiveLoadG } from './load.js';
import { countCompletedWorkingSets } from './sets.js';
import { strengthVolume } from './volume.js';
import { summariseCardio } from './cardio.js';
import type { PerformedExercise } from './types.js';
import type { PrKind } from './enums.js';
import type { CardioSummary } from './cardio.js';

/**
 * Progress, derived from what was actually performed.
 *
 * This lives in core rather than on the server because **both** clients need
 * it: the PC computes it from SQLite, and the iPhone computes it from the
 * sessions in its downloaded bundle, offline. If each had its own copy they
 * would eventually disagree about whether something was a personal best —
 * which is precisely the disagreement the whole architecture exists to prevent.
 *
 * Nothing here is cached. Records are folded out of the logged sets every time,
 * so editing or deleting a workout corrects them automatically with no
 * invalidation step. That is the Phase 12 decision, and it is what makes
 * history genuinely editable.
 */

/** A record currently held, with enough to find the set that produced it. */
export interface DerivedRecord {
  kind: PrKind;
  /** Present for rep-indexed kinds (`rep_max`, `min_assistance`). */
  repCount: number | null;
  value: number;
  unit: string;
  achievedAt: number;
  sessionId: string;
  setId: string | null;
}

/**
 * The best of each record kind across every session.
 *
 * `detectPersonalRecords` reports one session's bests; holding the incumbents
 * across sessions is what turns that into an all-time table. `beats` decides
 * the comparison per kind, including the inverted ones where needing less
 * assistance — or running a faster pace — is the improvement.
 *
 * Ties are not records. Repeating a best leaves the original date standing,
 * which is why the fold runs oldest first.
 */
export function foldPersonalRecords(history: readonly PerformedExercise[]): DerivedRecord[] {
  const best = new Map<string, DerivedRecord>();

  for (const performance of [...history].sort((a, b) => a.sessionStartedAt - b.sessionStartedAt)) {
    const { bests } = detectPersonalRecords({
      exerciseId: performance.exerciseId,
      sessionId: performance.sessionId,
      modality: performance.modality,
      sets: performance.sets,
      existing: [],
      at: performance.sessionStartedAt,
    });

    for (const candidate of bests) {
      const key = keyOf(candidate.kind, candidate.repCount);
      const incumbent = best.get(key);

      if (incumbent === undefined || beats(candidate.kind, candidate.value, incumbent.value)) {
        best.set(key, {
          kind: candidate.kind,
          repCount: candidate.repCount,
          value: candidate.value,
          unit: candidate.unit,
          achievedAt: candidate.achievedAt,
          sessionId: candidate.sessionId,
          setId: candidate.setId,
        });
      }
    }
  }

  // Most recent first: "what have I just beaten" is the usual question.
  return [...best.values()].sort((a, b) => b.achievedAt - a.achievedAt);
}

function keyOf(kind: PrKind, repCount: number | null): string {
  return `${kind}#${repCount === null ? '-' : String(repCount)}`;
}

export interface RepMaxRow {
  reps: number;
  valueG: number;
  achievedAt: number;
  /**
   * True when a heavier set at *more* reps makes this one uninteresting.
   * Flagged rather than dropped, so the table stays honest about what was
   * actually lifted.
   */
  isDominated: boolean;
}

/** Best load at each rep count — the view that makes a PR more than a number. */
export function deriveRepMaxTable(records: readonly DerivedRecord[]): RepMaxRow[] {
  const table = buildRepMaxTable(
    records.map((record) => ({
      kind: record.kind,
      repCount: record.repCount,
      value: record.value,
    })),
  );

  return table.map((entry) => ({
    reps: entry.reps,
    valueG: Math.round(entry.value),
    achievedAt:
      records.find((record) => record.kind === 'rep_max' && record.repCount === entry.reps)
        ?.achievedAt ?? 0,
    isDominated: isDominated(entry, table),
  }));
}

/** The best estimated one-rep max on record, or null if none applies. */
export function bestEstimatedOneRepMax(records: readonly DerivedRecord[]): number | null {
  const record = records.find((entry) => entry.kind === 'est_1rm');
  return record === undefined ? null : Math.round(record.value);
}

export interface ExerciseTrendPoint {
  sessionId: string;
  performedAt: number;
  /** Heaviest completed set. Null when its load could not be determined. */
  topSetLoadG: number | null;
  topSetReps: number | null;
  estimatedOneRepMaxG: number | null;
  volumeGramReps: number;
  /** True when a set was excluded for unknown load; the volume is a floor. */
  volumeIsPartial: boolean;
  completedWorkingSets: number;
  totalReps: number;
}

/**
 * One point per session, oldest first.
 *
 * A point per session rather than per set: the question a trend answers is
 * "am I getting stronger", and a set-level series buries that in noise.
 */
export function deriveExerciseTrend(history: readonly PerformedExercise[]): ExerciseTrendPoint[] {
  return history
    .map((performance) => {
      const sets = performance.sets;
      const volume = strengthVolume(sets);

      let topSetLoadG: number | null = null;
      let topSetReps: number | null = null;
      for (const set of sets) {
        if (!set.isCompleted || set.reps === null || set.reps <= 0) continue;
        const load = effectiveLoadG(set);
        if (!load.known) continue;
        if (topSetLoadG === null || load.value > topSetLoadG) {
          topSetLoadG = load.value;
          topSetReps = set.reps;
        }
      }

      return {
        sessionId: performance.sessionId,
        performedAt: performance.sessionStartedAt,
        topSetLoadG,
        topSetReps,
        estimatedOneRepMaxG:
          topSetLoadG === null || topSetReps === null
            ? null
            : estimateOneRepMaxG(topSetLoadG, topSetReps),
        volumeGramReps: volume.volumeGramReps,
        volumeIsPartial: volume.isPartial,
        completedWorkingSets: countCompletedWorkingSets(sets),
        totalReps: volume.totalReps,
      };
    })
    // A chart reads left to right.
    .sort((a, b) => a.performedAt - b.performedAt);
}

/**
 * One cardio exercise's performance over time — Cardio Phase C7.
 *
 * A deliberate sibling to `ExerciseTrendPoint`/`deriveExerciseTrend`, not a
 * merged/extended version of it. Every field there — `topSetLoadG`,
 * `volumeGramReps`, `estimatedOneRepMaxG` — is strength-shaped and would be
 * permanently null/zero for cardio; the reverse is equally true. Two clean
 * shapes, one shared envelope (`sessionId`/`performedAt`, matching
 * `ExerciseTrendPoint`'s own), the same pattern C3/C4/C5 already established
 * for cardio vs. strength (segments vs. sets, `CardioPanel` vs.
 * `ExercisePanel`, the Plan Editor's modality branch).
 *
 * Literally `CardioSummary` plus the two envelope fields — not a
 * reimplementation of it. `summariseCardio()` already does the one thing
 * that matters here: count only `isCompleted` segments. A segment C3 seeded
 * with the plan's target and never touched contributes nothing to any field
 * below, because `summariseCardio` skips it, not because of any filtering
 * done here.
 */
export interface CardioTrendPoint extends CardioSummary {
  sessionId: string;
  performedAt: number;
}

/**
 * One point per session, oldest first — same shape of answer
 * `deriveExerciseTrend` gives for strength, over cardio segments instead of
 * sets.
 *
 * Pace and speed are never computed or stored here: `CardioSummary.pace` is
 * already a `Maybe`, produced by `paceSecondsPerKm` inside `summariseCardio`,
 * and speed (`speedKph`) is left for whichever screen wants it to derive from
 * `totalDistanceM`/`totalDurationS` at render time — duplicating either
 * calculation here would be exactly the second implementation this phase's
 * brief says not to create.
 */
export function deriveCardioTrend(history: readonly PerformedExercise[]): CardioTrendPoint[] {
  return history
    .map((performance) => ({
      ...summariseCardio(performance.segments ?? []),
      sessionId: performance.sessionId,
      performedAt: performance.sessionStartedAt,
    }))
    // A chart reads left to right.
    .sort((a, b) => a.performedAt - b.performedAt);
}
